"use client";

import { Fragment, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";

/* ─── SVG icons (inline, no external deps) ──────────────────────────────── */
const IcSparkle = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M8 1L9.5 6.5L15 8L9.5 9.5L8 15L6.5 9.5L1 8L6.5 6.5L8 1Z" fill="currentColor" />
  </svg>
);
const IcUser = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="5" r="3" stroke="currentColor" strokeWidth="1.5" />
    <path d="M2 14c0-3.314 2.686-5 6-5s6 1.686 6 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);
const IcShield = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M8 1.5L2 4v4c0 3 2.7 5.4 6 6 3.3-.6 6-3 6-6V4L8 1.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M5.5 8l1.5 1.5L10.5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IcCheck = () => (
  <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
    <path d="M2.5 6l2.5 2.5 4.5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IcBuilding = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <rect x="2" y="3" width="12" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M5 14V9h6v5" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    <rect x="5.5" y="5" width="2" height="2" rx="0.5" fill="currentColor" />
    <rect x="8.5" y="5" width="2" height="2" rx="0.5" fill="currentColor" />
  </svg>
);
const IcMapPin = () => (
  <svg width="12" height="12" viewBox="0 0 14 16" fill="none">
    <path d="M7 1C4.24 1 2 3.24 2 6c0 3.75 5 9 5 9s5-5.25 5-9c0-2.76-2.24-5-5-5Z" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="7" cy="6" r="1.5" fill="currentColor" />
  </svg>
);

/* ─── Beliefs ────────────────────────────────────────────────────────────── */
const getBeliefs = (locale: string) => {
  if (locale === "hi") {
    return [
      {
        id: "ai",
        Icon: IcSparkle,
        color: "var(--rose)",
        soft: "var(--rose-soft)",
        tag: "सिद्धांत 1/3",
        title: "एआई-नेटिव, न कि ऊपर से जोड़ा गया",
        desc: "एआई उन कार्यप्रवाहों के भीतर रहता है जिनका उपयोग डॉक्टर और व्यवस्थापक पहले से ही कर रहे हैं - न कि एक अलग चैटबॉट टैब के रूप में।",
      },
      {
        id: "phr",
        Icon: IcUser,
        color: "var(--cyan-dark)",
        soft: "var(--cyan-soft)",
        tag: "सिद्धांत 2/3",
        title: "मरीज-नियंत्रित रिकॉर्ड",
        desc: "स्वास्थ्य रिकॉर्ड मरीजों के हैं। अस्पताल केवल संरक्षक हैं। हमारा सुरक्षित PHR स्तर इसे डिजाइन द्वारा लागू करता है।",
      },
      {
        id: "comply",
        Icon: IcShield,
        color: "#7B4FBF",
        soft: "#F2ECF9",
        tag: "सिद्धांत 3/3",
        title: "डिफ़ॉल्ट रूप से अनुपालन",
        desc: "ABDM, NHCX, FHIR, HL7, ISO 27001 - कोर में जुड़े हुए हैं, कभी भी बाहर से नहीं थोपे गए।",
      },
    ];
  }
  if (locale === "ar") {
    return [
      {
        id: "ai",
        Icon: IcSparkle,
        color: "var(--rose)",
        soft: "var(--rose-soft)",
        tag: "المبدأ 1 من 3",
        title: "ذكاء اصطناعي مدمج وليس مضافاً",
        desc: "يعيش الذكاء الاصطناعي داخل شاشات العمل اليومية للأطباء والموظفين - وليس كنافذة محادثة منفصلة.",
      },
      {
        id: "phr",
        Icon: IcUser,
        color: "var(--cyan-dark)",
        soft: "var(--cyan-soft)",
        tag: "المبدأ 2 من 3",
        title: "السجلات ملك المريض",
        desc: "الملفات الطبية ملك للمرضى والمستشفيات هي أمينة عليها. يضمن نظامنا القائم على نظام PHR هذا المبدأ.",
      },
      {
        id: "comply",
        Icon: IcShield,
        color: "#7B4FBF",
        soft: "#F2ECF9",
        tag: "المبدأ 3 من 3",
        title: "الامتثال والمعايير كجزء أساسي",
        desc: "ABDM و NHCX و FHIR و HL7 و ISO 27001 - كلها مدمجة في صلب النظام وليست مجرد خيارات إضافية.",
      },
    ];
  }
  if (locale === "es") {
    return [
      {
        id: "ai",
        Icon: IcSparkle,
        color: "var(--rose)",
        soft: "var(--rose-soft)",
        tag: "Principio 1 de 3",
        title: "IA nativa, no añadida",
        desc: "La IA vive dentro de los flujos de trabajo que los médicos y administradores ya utilizan, no como una pestaña de chatbot separada.",
      },
      {
        id: "phr",
        Icon: IcUser,
        color: "var(--cyan-dark)",
        soft: "var(--cyan-soft)",
        tag: "Principio 2 de 3",
        title: "Registros controlados por el paciente",
        desc: "Los historiales médicos pertenecen a los pacientes. Los hospitales son custodios. Nuestra capa PHR vinculada a ABDM lo garantiza por diseño.",
      },
      {
        id: "comply",
        Icon: IcShield,
        color: "#7B4FBF",
        soft: "#F2ECF9",
        tag: "Principio 3 de 3",
        title: "Cumplimiento por defecto",
        desc: "ABDM, NHCX, FHIR R4, HL7, ISO 27001, integrados en el núcleo de la plataforma. Nunca una casilla de verificación, nunca injertados.",
      },
    ];
  }
  if (locale === "fr") {
    return [
      {
        id: "ai",
        Icon: IcSparkle,
        color: "var(--rose)",
        soft: "var(--rose-soft)",
        tag: "Principe 1 sur 3",
        title: "IA native, pas intégrée après coup",
        desc: "L'IA vit au sein des flux de travail que les cliniciens et administrateurs utilisent déjà, pas dans un onglet de chatbot séparé.",
      },
      {
        id: "phr",
        Icon: IcUser,
        color: "var(--cyan-dark)",
        soft: "var(--cyan-soft)",
        tag: "Principe 2 sur 3",
        title: "Dossiers contrôlés par le patient",
        desc: "Les dossiers médicaux appartiennent aux patients. Les hôpitaux sont des dépositaires. Notre couche PHR sécurisée liée à l'ABDM le garantit par conception.",
      },
      {
        id: "comply",
        Icon: IcShield,
        color: "#7B4FBF",
        soft: "#F2ECF9",
        tag: "Principe 3 sur 3",
        title: "Conformité par défaut",
        desc: "ABDM, NHCX, FHIR R4, HL7, ISO 27001, câblés dans la plateforme centrale. Jamais une simple case à cocher, jamais greffés après coup.",
      },
    ];
  }
  return [
    {
      id: "ai",
      Icon: IcSparkle,
      color: "var(--rose)",
      soft: "var(--rose-soft)",
      tag: "Principle 1 of 3",
      title: "AI-native, not AI-bolted",
      desc: "AI lives inside the workflows clinicians and admins already use — not as a separate chatbot tab. Every screen, every report, documents itself.",
    },
    {
      id: "phr",
      Icon: IcUser,
      color: "var(--cyan-dark)",
      soft: "var(--cyan-soft)",
      tag: "Principle 2 of 3",
      title: "Patient-controlled records",
      desc: "Health records belong to patients. Hospitals are stewards, not owners. Our secure ABDM-linked PHR layer enforces this by design.",
    },
    {
      id: "comply",
      Icon: IcShield,
      color: "#7B4FBF",
      soft: "#F2ECF9",
      tag: "Principle 3 of 3",
      title: "Compliance by default",
      desc: "ABDM, NHCX, FHIR R4, HL7, ISO 27001 — wired into the core platform. Never a checkbox, never grafted on.",
    },
  ];
};

/* ─── Stats ──────────────────────────────────────────────────────────────── */
const getStats = (locale: string) => {
  if (locale === "hi") {
    return [
      { value: "150+",  label: "सक्रिय अस्पताल",   sub: "30 से 1,200 बिस्तरों वाले" },
      { value: "19+",   label: "मॉड्यूल",          sub: "सक्रिय उत्पादन में" },
      { value: "62",    label: "शहर",           sub: "पूरे भारत में"        },
      { value: "11",    label: "भाषाएँ",        sub: "हिंदी, तमिल सहित"  },
    ];
  }
  if (locale === "ar") {
    return [
      { value: "150+",  label: "المستشفيات المشغلة",   sub: "30 إلى 1,200 سرير" },
      { value: "19+",   label: "الوحدات البرمجية",          sub: "قيد التشغيل الفعلي" },
      { value: "62",    label: "المدن",           sub: "في جميع أنحاء الهند"        },
      { value: "11",    label: "اللغات",        sub: "بما فيها الهندية والتاميلية"  },
    ];
  }
  if (locale === "es") {
    return [
      { value: "150+",  label: "Hospitales activos",   sub: "de 30 a 1,200 camas" },
      { value: "19+",   label: "Módulos",          sub: "en producción activa" },
      { value: "62",    label: "Ciudades",           sub: "en toda la India"        },
      { value: "11",    label: "Idiomas",        sub: "incl. hindi y tamil"  },
    ];
  }
  if (locale === "fr") {
    return [
      { value: "150+",  label: "Hôpitaux actifs",   sub: "de 30 à 1 200 lits" },
      { value: "19+",   label: "Modules",          sub: "en production active" },
      { value: "62",    label: "Villes",           sub: "à travers l'Inde"        },
      { value: "11",    label: "Langues",        sub: "dont hindi et tamoul"  },
    ];
  }
  return [
    { value: "150+",  label: "Hospitals live",   sub: "30-bed to 1,200-bed" },
    { value: "19+",   label: "Modules",          sub: "in active production" },
    { value: "62",    label: "Cities",           sub: "across India"        },
    { value: "11",    label: "Languages",        sub: "incl. Hindi, Tamil"  },
  ];
};

/* ─── Certifications ─────────────────────────────────────────────────────── */
const getCerts = (locale: string) => {
  if (locale === "hi") {
    return [
      { text: "ABDM एम्पैनल्ड", color: "var(--cyan-dark)",  bg: "var(--cyan-soft)",         dot: true  },
      { text: "NHCX",            color: "var(--rose)",       bg: "var(--rose-soft)",         dot: false },
      { text: "ISO 27001",       color: "#7B4FBF",           bg: "#F2ECF9",                  dot: false },
      { text: "FHIR R4",         color: "var(--ink-2)",      bg: "rgba(5,60,97,0.07)",       dot: false },
      { text: "PMJAY गेटवे",   color: "#1A6033",           bg: "var(--mint-soft)",         dot: false },
    ];
  }
  if (locale === "ar") {
    return [
      { text: "معتمد من ABDM", color: "var(--cyan-dark)",  bg: "var(--cyan-soft)",         dot: true  },
      { text: "NHCX",            color: "var(--rose)",       bg: "var(--rose-soft)",         dot: false },
      { text: "ISO 27001",       color: "#7B4FBF",           bg: "#F2ECF9",                  dot: false },
      { text: "FHIR R4",         color: "var(--ink-2)",      bg: "rgba(5,60,97,0.07)",       dot: false },
      { text: "بوابة PMJAY",   color: "#1A6033",           bg: "var(--mint-soft)",         dot: false },
    ];
  }
  if (locale === "es") {
    return [
      { text: "Acreditado ABDM", color: "var(--cyan-dark)",  bg: "var(--cyan-soft)",         dot: true  },
      { text: "NHCX",            color: "var(--rose)",       bg: "var(--rose-soft)",         dot: false },
      { text: "ISO 27001",       color: "#7B4FBF",           bg: "#F2ECF9",                  dot: false },
      { text: "FHIR R4",         color: "var(--ink-2)",      bg: "rgba(5,60,97,0.07)",       dot: false },
      { text: "Pasarela PMJAY",   color: "#1A6033",           bg: "var(--mint-soft)",         dot: false },
    ];
  }
  if (locale === "fr") {
    return [
      { text: "Agréé ABDM", color: "var(--cyan-dark)",  bg: "var(--cyan-soft)",         dot: true  },
      { text: "NHCX",            color: "var(--rose)",       bg: "var(--rose-soft)",         dot: false },
      { text: "ISO 27001",       color: "#7B4FBF",           bg: "#F2ECF9",                  dot: false },
      { text: "FHIR R4",         color: "var(--ink-2)",      bg: "rgba(5,60,97,0.07)",       dot: false },
      { text: "Passerelle PMJAY",   color: "#1A6033",           bg: "var(--mint-soft)",         dot: false },
    ];
  }
  return [
    { text: "ABDM Empanelled", color: "var(--cyan-dark)",  bg: "var(--cyan-soft)",         dot: true  },
    { text: "NHCX",            color: "var(--rose)",       bg: "var(--rose-soft)",         dot: false },
    { text: "ISO 27001",       color: "#7B4FBF",           bg: "#F2ECF9",                  dot: false },
    { text: "FHIR R4",         color: "var(--ink-2)",      bg: "rgba(5,60,97,0.07)",       dot: false },
    { text: "PMJAY Gateway",   color: "#1A6033",           bg: "var(--mint-soft)",         dot: false },
  ];
};

const getAboutHeroTranslations = (locale: string) => {
  if (locale === "hi") {
    return {
      rebuilding: "हम एआई पर अस्पताल का",
      onAi: "पुनर्निर्माण कर रहे हैं।",
      subTitle: "चिकित्सकों, इंजीनियरों और नीति विचारकों द्वारा भारतीय स्वास्थ्य सेवा के लिए निर्मित एआई-नेटिव HIMS।",
      live: "लाइव",
      founded: "स्थापना 2024",
      golive: "1 दिन में गो-लाइव",
      languages: "11 क्षेत्रीय भाषाएँ",
      auditReady: "SOC-2 और ISO ऑडिट-रेडी",
      certifiedLabel: "प्रमाणित और एकीकृत",
      pune: "पुणे",
      jaipur: "जयपुर",
    };
  }
  if (locale === "ar") {
    return {
      rebuilding: "نعيد بناء المستشفى ",
      onAi: "على أسس الذكاء الاصطناعي.",
      subTitle: "نظام HIMS أصيل للذكاء الاصطناعي مبني للأطباء والمهندسين والسياسيين للرعاية الصحية الهندية.",
      live: "مباشر",
      founded: "تأسست عام 2024",
      golive: "التشغيل في يوم واحد",
      languages: "11 لغة إقليمية",
      auditReady: "جاهز لتدقيق SOC-2 و ISO",
      certifiedLabel: "معتمد ومتكامل",
      pune: "بونا",
      jaipur: "جايبور",
    };
  }
  if (locale === "es") {
    return {
      rebuilding: "Reconstruyendo el hospital ",
      onAi: "sobre IA.",
      subTitle: "HIMS nativo de IA creado por médicos, ingenieros y expertos en políticas para la salud de la India.",
      live: "En Vivo",
      founded: "Fundado 2024",
      golive: "Puesta en marcha en 1 día",
      languages: "11 idiomas regionales",
      auditReady: "Listo para auditoría SOC-2 e ISO",
      certifiedLabel: "Certificado e Integrado",
      pune: "Pune",
      jaipur: "Jaipur",
    };
  }
  if (locale === "fr") {
    return {
      rebuilding: "Nous rebâtissons l'hôpital ",
      onAi: "sur l'IA.",
      subTitle: "HIMS natif d'IA conçu par des cliniciens, des ingénieurs et des experts en politiques pour la santé en Inde.",
      live: "Live",
      founded: "Fondé en 2024",
      golive: "Déploiement en 1 jour",
      languages: "11 langues régionales",
      auditReady: "Prêt pour audit SOC-2 & ISO",
      certifiedLabel: "Certifié & Intégré",
      pune: "Pune",
      jaipur: "Jaipur",
    };
  }
  return {
    rebuilding: "We're rebuilding the hospital, ",
    onAi: "on AI.",
    subTitle: "AI-native HIMS built by clinicians, engineers & policy thinkers for Indian healthcare.",
    live: "Live",
    founded: "Founded 2024",
    golive: "Go-live in 1 day",
    languages: "11 regional languages",
    auditReady: "SOC-2 & ISO audit-ready",
    certifiedLabel: "Certified & Integrated",
    pune: "Pune",
    jaipur: "Jaipur",
  };
};

/* ─── Animated stat cell ─────────────────────────────────────────────────── */
function StatCell({ value, label, sub, delay, isLast }: {
  value: string; label: string; sub: string; delay: number; isLast: boolean;
}) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShow(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={show ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.45, ease: "easeOut" }}
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
        padding: "0 6px",
        borderRight: isLast ? "none" : "1px solid rgba(11,23,51,0.07)",
      }}
    >
      <span style={{
        fontSize: 23,
        fontWeight: 800,
        color: "var(--ink)",
        fontFamily: "var(--font-sans)",
        lineHeight: 1.1,
        letterSpacing: "-0.02em",
      }}>
        {value}
      </span>
      <span style={{
        fontSize: 10,
        fontWeight: 700,
        color: "var(--ink-3)",
        fontFamily: "var(--font-sans)",
        textTransform: "uppercase",
        letterSpacing: "0.06em",
      }}>
        {label}
      </span>
      <span style={{
        fontSize: 9,
        fontWeight: 500,
        color: "var(--ink-4)",
        fontFamily: "var(--font-sans)",
        marginTop: 1,
      }}>
        {sub}
      </span>
    </motion.div>
  );
}

/* ─── Main ───────────────────────────────────────────────────────────────── */
export function AboutHeroAnimation({ locale = "en" }: { locale?: string }) {
  const [mounted, setMounted] = useState(false);
  const [beliefIdx, setBeliefIdx] = useState(0);

  const BELIEFS = getBeliefs(locale);
  const STATS = getStats(locale);
  const CERTS = getCerts(locale);
  const ui = getAboutHeroTranslations(locale);

  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    const id = setInterval(() => setBeliefIdx((i) => (i + 1) % BELIEFS.length), 3800);
    return () => clearInterval(id);
  }, [BELIEFS.length]);

  if (!mounted) {
    return <div style={{ width: "100%", height: 480, borderRadius: 20, background: "#fff" }} />;
  }

  const belief = BELIEFS[beliefIdx];

  return (
    <>
      <style>{`
        .ac-card {
          width: 100%;
          background: #ffffff;
          border-radius: 20px;
          box-shadow:
            0 32px 64px -16px rgba(11,23,51,0.14),
            0 0 0 1px rgba(11,23,51,0.05);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        /* ── Chrome ── */
        .ac-chrome {
          display: flex; align-items: center; justify-content: space-between;
          padding: 13px 20px;
          background: rgba(251,250,247,0.98);
          border-bottom: 1px solid rgba(11,23,51,0.06);
          flex-shrink: 0;
        }
        .ac-dots { display: flex; gap: 7px; }
        .ac-dot  { width: 11px; height: 11px; border-radius: 50%; }
        .ac-dot.r { background: #FF5F56; border: 1px solid #E0443E; }
        .ac-dot.y { background: #FFBD2E; border: 1px solid #DEA123; }
        .ac-dot.g { background: #27C93F; border: 1px solid #1AAB29; }
        .ac-chrome-label {
          font-family: var(--font-mono);
          font-size: 11px; color: var(--ink-4); letter-spacing: 0.05em; font-weight: 500;
        }

        /* ── Mission banner ── */
        .ac-mission {
          padding: 20px 22px 16px;
          border-bottom: 1px solid rgba(11,23,51,0.05);
          display: flex; align-items: flex-start; justify-content: space-between; gap: 14px;
        }
        .ac-mission-headline {
          font-size: 15px; font-weight: 700; color: var(--ink);
          font-family: var(--font-sans); line-height: 1.38; flex: 1;
        }
        .ac-live-pill {
          display: flex; align-items: center; gap: 6px;
          padding: 4px 10px; border-radius: 999px;
          background: var(--rose-soft); border: 1px solid rgba(204,43,88,0.16);
          flex-shrink: 0; margin-top: 2px;
        }
        .ac-live-dot {
          width: 6px; height: 6px; border-radius: 50%; background: var(--rose);
          animation: ac-live-glow 2s cubic-bezier(0.25, 0, 0, 1) infinite;
        }
        @keyframes ac-live-glow {
          0% {
            box-shadow: 0 0 0 0 rgba(204, 43, 88, 0.8);
          }
          70% {
            box-shadow: 0 0 0 8px rgba(204, 43, 88, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(204, 43, 88, 0);
          }
        }
        .ac-live-text {
          font-size: 9.5px; font-weight: 700; color: var(--rose);
          font-family: var(--font-sans); letter-spacing: 0.06em; text-transform: uppercase;
          white-space: nowrap;
        }

        /* ── Location row ── */
        .ac-location {
          display: flex; align-items: center; gap: 5px;
          padding: 8px 22px 10px; border-bottom: 1px solid rgba(11,23,51,0.04);
          flex-wrap: wrap;
        }
        .ac-location-chip {
          display: flex; align-items: center; gap: 4px;
          font-size: 10px; font-weight: 600; color: var(--ink-3);
          font-family: var(--font-sans); padding: 2px 7px 2px 5px;
          border-radius: 6px; background: rgba(11,23,51,0.04);
        }
        .ac-location-sep { color: var(--ink-4); font-size: 9px; }

        /* ── Stats ── */
        .ac-stats {
          display: flex; align-items: stretch;
          padding: 14px 10px; border-bottom: 1px solid rgba(11,23,51,0.05);
        }

        /* ── Certs ── */
        .ac-certs {
          display: flex; align-items: center; gap: 6px;
          padding: 12px 22px; border-bottom: 1px solid rgba(11,23,51,0.05);
          flex-wrap: wrap;
        }
        .ac-cert-label {
          font-size: 9px; font-weight: 700; color: var(--ink-4);
          font-family: var(--font-sans); text-transform: uppercase;
          letter-spacing: 0.06em; margin-right: 2px;
        }
        .ac-cert {
          display: inline-flex; align-items: center; gap: 4px;
          font-size: 9.5px; font-weight: 700; letter-spacing: 0.04em;
          padding: 3px 8px; border-radius: 6px; border: 1px solid;
          font-family: var(--font-sans);
        }
        .ac-cert-dot {
          width: 5px; height: 5px; border-radius: 50%;
          flex-shrink: 0;
          animation: ac-pulse 1.8s ease-in-out infinite;
        }

        /* ── Belief ── */
        .ac-belief-wrap {
          position: relative; overflow: hidden;
        }
        .ac-belief {
          padding: 14px 22px 10px;
          display: flex; align-items: flex-start; gap: 13px;
          min-height: 92px;
        }
        .ac-belief-icon {
          width: 34px; height: 34px; border-radius: 9px;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0; margin-top: 1px;
        }
        .ac-belief-tag {
          font-size: 9px; font-weight: 700; text-transform: uppercase;
          letter-spacing: 0.07em; color: var(--ink-4);
          font-family: var(--font-sans); margin-bottom: 4px;
        }
        .ac-belief-title {
          font-size: 12.5px; font-weight: 700; color: var(--ink);
          font-family: var(--font-sans); line-height: 1.3; margin-bottom: 4px;
        }
        .ac-belief-desc {
          font-size: 11px; color: var(--ink-3);
          font-family: var(--font-sans); line-height: 1.5;
        }

        /* ── Nav dots ── */
        .ac-dots-nav {
          display: flex; gap: 5px; padding: 0 22px 14px; align-items: center;
        }
        .ac-nav-dot {
          height: 3px; border-radius: 2px;
          background: rgba(11,23,51,0.1);
          transition: all 0.35s ease; cursor: pointer;
          border: none; padding: 0;
        }
        .ac-nav-dot:hover { background: rgba(11,23,51,0.25); }

        /* ── Footer strip ── */
        .ac-footer {
          display: flex; align-items: center; gap: 10px;
          padding: 10px 22px;
          background: rgba(251,250,247,0.6);
          border-top: 1px solid rgba(11,23,51,0.05);
        }
        .ac-footer-item {
          display: flex; align-items: center; gap: 5px;
          font-size: 9.5px; font-weight: 600; color: var(--ink-3);
          font-family: var(--font-sans);
        }
        .ac-footer-sep { color: rgba(11,23,51,0.18); font-size: 10px; }

        @media (max-width: 680px) {
          .ac-card {
            border-radius: 16px;
          }
          .ac-mission {
            padding: 14px 14px 12px;
            flex-direction: column;
            gap: 8px;
          }
          .ac-location {
            padding: 8px 14px;
          }
          .ac-stats {
            display: grid !important;
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 12px 0 !important;
            padding: 12px 6px !important;
          }
          .ac-stats > div {
            border-right: none !important;
          }
          .ac-certs {
            padding: 10px 14px;
            gap: 4px;
          }
          .ac-belief {
            padding: 12px 14px 8px;
            min-height: auto;
          }
          .ac-dots-nav {
            padding: 0 14px 10px;
          }
          .ac-footer {
            padding: 8px 14px;
            flex-wrap: wrap;
            gap: 6px;
          }
        }
      `}</style>

      <div className="ac-card">

        {/* Chrome */}
        <div className="ac-chrome">
          <div className="ac-dots">
            <div className="ac-dot r" />
            <div className="ac-dot y" />
            <div className="ac-dot g" />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Image
              src="/assets/logo-chikitsa.svg"
              alt="Chikitsa"
              width={96}
              height={22}
              style={{ height: 20, width: "auto" }}
            />
            <span style={{ width: 1, height: 14, background: "rgba(11,23,51,0.15)" }} />
            <Image
              src="/assets/logo-vigorus.png"
              alt="Vigorus AI"
              width={64}
              height={16}
              style={{ height: 14, width: "auto", mixBlendMode: "multiply" }}
              unoptimized
            />
          </div>
          <div style={{ width: 44 }} />
        </div>

        {/* Mission */}
        <div className="ac-mission">
          <motion.div
            className="ac-mission-headline"
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.08 }}
          >
            {ui.rebuilding}{" "}
            <span style={{ color: "var(--rose)" }}>{ui.onAi}</span>
            <div style={{
              fontSize: 11, fontWeight: 500, color: "var(--ink-3)",
              marginTop: 5, lineHeight: 1.45
            }}>
              {ui.subTitle}
            </div>
          </motion.div>
          <motion.div
            className="ac-live-pill"
            initial={{ opacity: 0, scale: 0.88 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: 0.25 }}
          >
            <span className="ac-live-dot" />
            <span className="ac-live-text">{ui.live}</span>
          </motion.div>
        </div>

        {/* Location row */}
        <motion.div
          className="ac-location"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.35 }}
        >
          <span style={{ display: "inline-flex", alignItems: "center" }}>
            <Image
              src="/assets/logo-vigorus.png"
              alt="Vigorus AI"
              width={72}
              height={18}
              style={{ height: 16, width: "auto", objectFit: "contain", mixBlendMode: "multiply" }}
              unoptimized
            />
          </span>
          <span className="ac-location-sep">·</span>
          <span className="ac-location-chip">
            <IcMapPin />&nbsp;{ui.pune}
          </span>
          <span className="ac-location-sep">·</span>
          <span className="ac-location-chip">
            <IcMapPin />&nbsp;{ui.jaipur}
          </span>
          <span className="ac-location-sep">·</span>
          <span style={{ fontSize: 9.5, color: "var(--ink-4)", fontFamily: "var(--font-sans)", fontWeight: 500 }}>
            {ui.founded}
          </span>
        </motion.div>

        {/* Stats */}
        <div className="ac-stats">
          {STATS.map((s, i) => (
            <StatCell
              key={s.label}
              value={s.value}
              label={s.label}
              sub={s.sub}
              delay={200 + i * 100}
              isLast={i === STATS.length - 1}
            />
          ))}
        </div>

        {/* Certifications */}
        <motion.div
          className="ac-certs"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.45, delay: 0.6 }}
        >
          <span className="ac-cert-label">{ui.certifiedLabel}</span>
          {CERTS.map((c) => (
            <span
              key={c.text}
              className="ac-cert"
              style={{ color: c.color, background: c.bg, borderColor: `${c.color}28` }}
            >
              {c.dot && <span className="ac-cert-dot" style={{ background: c.color }} />}
              {!c.dot && (
                <span style={{ color: c.color, display: "flex" }}>
                  <IcCheck />
                </span>
              )}
              {c.text}
            </span>
          ))}
        </motion.div>

        {/* Belief strip */}
        <div className="ac-belief-wrap">
          <AnimatePresence mode="wait">
            <motion.div
              key={belief.id}
              className="ac-belief"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.36, ease: "easeOut" }}
            >
              <div
                className="ac-belief-icon"
                style={{ background: belief.soft, color: belief.color }}
              >
                <belief.Icon />
              </div>
              <div style={{ flex: 1 }}>
                <div className="ac-belief-tag">{belief.tag}</div>
                <div className="ac-belief-title">{belief.title}</div>
                <div className="ac-belief-desc">{belief.desc}</div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Nav dots */}
        <div className="ac-dots-nav">
          {BELIEFS.map((b, i) => (
            <button
              key={b.id}
              className="ac-nav-dot"
              style={{
                width: i === beliefIdx ? 20 : 6,
                background: i === beliefIdx ? "var(--ink)" : undefined,
              }}
              onClick={() => setBeliefIdx(i)}
              aria-label={`Belief ${i + 1}`}
            />
          ))}
        </div>

        {/* Footer */}
        <motion.div
          className="ac-footer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.75 }}
        >
          <span className="ac-footer-item">
            <span style={{ color: "var(--mint-dark)", display: "flex" }}><IcCheck /></span>
            {ui.golive}
          </span>
          <span className="ac-footer-sep">·</span>
          <span className="ac-footer-item">
            <span style={{ color: "var(--mint-dark)", display: "flex" }}><IcCheck /></span>
            {ui.languages}
          </span>
          <span className="ac-footer-sep">·</span>
          <span className="ac-footer-item">
            <span style={{ color: "var(--mint-dark)", display: "flex" }}><IcCheck /></span>
            {ui.auditReady}
          </span>
        </motion.div>

      </div>
    </>
  );
}
